import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import {
  BarChart,
  CheckCircle,
  X,
  Droplet,
  Calendar,
  MoreVertical,
  Clock,
  Filter,
  AlertTriangle,
  User,
  MapPin,
  FileText
} from 'react-feather';

const RequestHistory = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestHistory, setRequestHistory] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    fulfilled: 0,
    partiallyFulfilled: 0,
    cancelled: 0,
    totalDonors: 0,
    totalDonated: 0,
    donatedRequests: 0
  });
  const [filter, setFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [expandedRequestId, setExpandedRequestId] = useState(null);

  // Fetch request history from API
  useEffect(() => {
    const fetchRequestHistory = async () => {
      try {
        setLoading(true);

        // Use the api module which already handles authentication headers
        const res = await api.get('requests/history');
        setRequestHistory(res.data.requests);
        setStats(res.data.stats);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching request history:', err);
        setError('Failed to load request history. Please try again later.');
        setLoading(false);
      }
    };

    fetchRequestHistory();
  }, [token]);

  // Filter requests based on selected filters
  const filteredRequests = requestHistory.filter(request => {
    // Filter by status
    if (filter !== 'all') {
      if (filter === 'donated') {
        // For donated filter, check if there are any donors with 'donated' status
        if (!request.donorStats || request.donorStats.donated === 0) {
          return false;
        }
      } else if (filter === 'fulfilled') {
        // For fulfilled filter, only show requests with fulfilled status AND no donations
        if (request.status !== 'fulfilled' || (request.donorStats && request.donorStats.donated > 0)) {
          return false;
        }
      } else if (filter === 'pending') {
        // For pending filter, show requests with pending status
        if (request.status !== 'pending') {
          return false;
        }
      } else if (request.status !== filter) {
        return false;
      }
    }

    // Filter by time range
    if (timeRange !== 'all') {
      const requestDate = new Date(request.createdAt || Date.now());
      const now = new Date();
      const daysDiff = Math.floor((now - requestDate) / (1000 * 60 * 60 * 24));

      if (timeRange === '30' && daysDiff > 30) return false;
      if (timeRange === '90' && daysDiff > 90) return false;
    }

    return true;
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';

    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Toggle expanded view for a request
  const toggleExpandRequest = (id) => {
    if (expandedRequestId === id) {
      setExpandedRequestId(null);
    } else {
      setExpandedRequestId(id);
    }
  };

  // Get status badge color
  const getStatusBadgeClass = (status, donorStats) => {
    // If there are donations, show as donated instead of fulfilled
    if (donorStats && donorStats.donated > 0) {
      return 'bg-blue-100 text-blue-800';
    }

    switch (status) {
      case 'fulfilled':
        return 'bg-green-100 text-green-800';
      case 'partially_fulfilled':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get formatted status text
  const getStatusText = (status, donorStats) => {
    // If there are donations, show as donated instead of fulfilled
    if (donorStats && donorStats.donated > 0) {
      return 'Donated';
    }

    switch (status) {
      case 'fulfilled':
        return 'Fulfilled';
      case 'partially_fulfilled':
        return 'Partially Fulfilled';
      case 'cancelled':
        return 'Cancelled';
      case 'pending':
        return 'Pending';
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
  };

  // Render Request History
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Request History</h2>
            <p className="text-xs text-gray-500 mt-0.5">Overview of your past blood requests</p>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative">
              <select
                className="pl-9 pr-4 py-2 text-sm border-gray-200 rounded-lg focus:ring-red-500 focus:border-red-500"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="pending">Pending</option>
                <option value="partially_fulfilled">Partially Fulfilled</option>
                <option value="cancelled">Cancelled</option>
                <option value="donated">Donated</option>
              </select>
              <Filter className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            </div>
            <div className="relative">
              <select
                className="pl-9 pr-4 py-2 text-sm border-gray-200 rounded-lg focus:ring-red-500 focus:border-red-500"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="all">All Time</option>
              </select>
              <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-gray-50 p-3 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Requests</p>
                <p className="text-xl font-bold">{stats?.total || 0}</p>
              </div>
              <div className="bg-gray-100 p-1.5 rounded-full">
                <FileText className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-3 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600">Fulfilled</p>
                <p className="text-xl font-bold text-green-700">{stats?.fulfilled + stats?.partiallyFulfilled || 0}</p>
              </div>
              <div className="bg-green-100 p-1.5 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600">Donated</p>
                <p className="text-xl font-bold text-blue-700">{stats?.donatedRequests || 0}</p>
              </div>
              <div className="bg-blue-100 p-1.5 rounded-full">
                <Droplet className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-3 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Cancelled</p>
                <p className="text-xl font-bold text-red-700">{stats?.cancelled || 0}</p>
              </div>
              <div className="bg-red-100 p-1.5 rounded-full">
                <X className="w-5 h-5 text-red-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Loading, Error and Empty States */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900">Loading request history...</h3>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Error Loading Data</h3>
            <p className="text-gray-500 mt-2">{error}</p>
          </div>
        )}

        {!loading && !error && filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No Request History</h3>
            <p className="text-gray-500 mt-2">
              {filter !== 'all' || timeRange !== 'all'
                ? 'No requests match your current filters. Try changing your filters.'
                : 'Your past blood requests will appear here'}
            </p>
          </div>
        )}

        {/* Request History List */}
        {!loading && !error && filteredRequests.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {filteredRequests.map(request => (
                <li key={request._id} className="hover:bg-gray-50">
                  <div className="px-3 py-3 sm:px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-2.5 ${request.bloodType && request.bloodType.includes('O') ? 'bg-red-100' : 'bg-orange-100'}`}>
                          <Droplet className={`w-3.5 h-3.5 ${request.bloodType && request.bloodType.includes('O') ? 'text-red-600' : 'text-orange-600'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Patient: {request.patientName}
                          </p>
                          <p className="text-xs text-gray-500">
                            Blood Group: {request.bloodType || 'Not specified'} • Units: {request.unitsNeeded || 0}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-xs text-gray-500 flex items-center">
                          <Calendar className="w-3.5 h-3.5 mr-1 text-gray-400" />
                          {formatDate(request.createdAt)}
                        </div>
                        <span className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadgeClass(request.status, request.donorStats)}`}>
                          {getStatusText(request.status, request.donorStats)}
                        </span>
                      </div>
                    </div>

                    {/* Second row with donor count */}
                    <div className="mt-1.5 flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        Total Donors: <span className="font-medium">{request.donorStats?.total || 0}</span>
                        {request.donorStats?.donated > 0 && (
                          <span className="ml-2 text-blue-600">
                            • Donated: <span className="font-medium">{request.donorStats?.donated || 0}</span>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => toggleExpandRequest(request._id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Expanded Request Details */}
                    {expandedRequestId === request._id && (
                      <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Request Details</h4>
                            <div className="space-y-2">
                              <div className="flex items-start">
                                <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{request.hospital?.name || 'Hospital not specified'}</p>
                                  <p className="text-xs text-gray-500">{request.hospital?.address || ''}, {request.hospital?.city || ''}</p>
                                </div>
                              </div>
                              <div className="flex items-start">
                                <AlertTriangle className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Urgency Level</p>
                                  <p className="text-xs text-gray-500">{request.urgency ? request.urgency.charAt(0).toUpperCase() + request.urgency.slice(1) : 'Normal'}</p>
                                </div>
                              </div>
                              <div className="flex items-start">
                                <User className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Contact Person</p>
                                  <p className="text-xs text-gray-500">
                                    {request.contactInfo?.name || 'Not specified'} • {request.contactInfo?.phone || 'No phone'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start">
                                <Calendar className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Request Timeline</p>
                                  <p className="text-xs text-gray-500">
                                    Created: {formatDate(request.createdAt)}
                                  </p>
                                  {request.completionDate && (
                                    <p className="text-xs text-green-500">
                                      Completed: {formatDate(request.completionDate)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Statistics</h4>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-white p-2 rounded border border-gray-200">
                                <p className="text-xs text-gray-500">Total Donors</p>
                                <p className="text-sm font-bold">{request.donorStats?.total || 0}</p>
                              </div>
                              <div className="bg-white p-2 rounded border border-gray-200">
                                <p className="text-xs text-gray-500">Donated</p>
                                <p className="text-sm font-bold text-blue-600">{request.donorStats?.donated || 0}</p>
                              </div>
                              <div className="bg-white p-2 rounded border border-gray-200">
                                <p className="text-xs text-gray-500">Accepted</p>
                                <p className="text-sm font-bold text-blue-600">{request.donorStats?.accepted || 0}</p>
                              </div>
                              <div className="bg-white p-2 rounded border border-gray-200">
                                <p className="text-xs text-gray-500">Declined/Rejected</p>
                                <p className="text-sm font-bold text-red-600">{(request.donorStats?.declined || 0) + (request.donorStats?.rejected || 0)}</p>
                              </div>
                            </div>

                            {request.reason && (
                              <div className="mt-4">
                                <h4 className="font-medium text-gray-900 mb-1">Reason for Request</h4>
                                <p className="text-sm text-gray-600 bg-white p-2 rounded border border-gray-200">
                                  {request.reason}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RequestHistory;